import { getBlockSkipKey } from '../../helpers/blockSkipKey';
import type { ComponentFlexibleHeroBlock } from '$/(cms)/_modules/cmsService/types';

// Minimal shape needed to decide the skip — accepts both the mapped
// CmsBaseComponent union and PageContent's CmsComponentProps.
type SkippableComponent =
  | {
      __typename?: string;
      id?: string | null;
      visible?: boolean | null;
      renderingMode?: string;
    }
  | null
  | undefined;

const HERO_TYPENAME = 'ComponentPagesComponentFlexibleHeroBlockComponent';

/**
 * A Flexible Hero the page renders: every visible one goes on top of the page (`PageContent`), and
 * the first is the page's hero.
 */
export const isRenderedFlexibleHero = (
  component: SkippableComponent,
): component is ComponentFlexibleHeroBlock =>
  !!component &&
  component.visible !== false &&
  component.__typename === HERO_TYPENAME;

// What BlockMapper actually renders: no empty entries, no `visible: false`
// components and no never-rendered ProductMetaConfig.
const getRendered = (components: readonly SkippableComponent[]) =>
  components.filter(
    (c): c is NonNullable<SkippableComponent> =>
      !!c &&
      c.visible !== false &&
      c.__typename !== 'ComponentPagesComponentProductMetaConfig',
  );

/**
 * Skip keys (see `getBlockSkipKey`) of the Flexible Heroes BlockMapper would
 * render: their slot there is always empty (a phantom `gap-spacing/gap/y`
 * child). `skipKeys` rather than filtering keeps the `content-${index}`
 * anchors stable.
 */
export const getHoistedHeroSkipKeys = (
  components: readonly SkippableComponent[],
): string[] =>
  components
    .filter(isRenderedFlexibleHero)
    .filter((c) => !!c.id)
    .map((c) => getBlockSkipKey(c));

/**
 * The hero skip keys when the first rendered non-hero block is a page-mode
 * Product Details (the PDP layout, CMS-1809), else none. Not array order:
 * editors also place the hero after the Product Details (CMS-1809 comment
 * 215919).
 */
export const getPdpFlushHeroSkipKeys = (
  components: readonly SkippableComponent[],
): string[] => {
  const heroKeys = getHoistedHeroSkipKeys(components);
  if (heroKeys.length === 0) {
    return [];
  }

  const firstNonHero = getRendered(components).find(
    (c) => c.__typename !== HERO_TYPENAME,
  );
  const isPageModeProductDetails =
    firstNonHero?.__typename === 'ProductDetailsComponent' &&
    firstNonHero.renderingMode !== 'MODAL';

  return isPageModeProductDetails ? heroKeys : [];
};
